import { inject, Injectable } from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { distinctUntilChanged, scan, switchMap } from "rxjs";

import { CalculationPastStoreService } from "@bitwarden/web-vault/app/services/store/calculation-past/calculation-past.store.service";
import { CalculationTodayStoreService } from "@bitwarden/web-vault/app/services/store/calculation-today/calculation-today.store.service";
import { AppStateStoreService } from "@bitwarden/web-vault/app/services/store/app-state/app-state.store.service";
import { ScenarioStoreService } from "@bitwarden/web-vault/app/services/store/scenario/scenario.store.service";

/**
 * Shared one-way readiness latches for GLOSS view skeletons.
 * Resets when {@link AppStateStoreService.loadingSession} bumps on logout.
 */
@Injectable({
  providedIn: "root",
})
export class ViewReadinessService {
  private appState = inject(AppStateStoreService);
  private calculationTodayStore = inject(CalculationTodayStoreService);
  private calculationPastStore = inject(CalculationPastStoreService);
  private scenarioStore = inject(ScenarioStoreService);
  private readonly loadingSession$ = toObservable(this.appState.loadingSession);

  /**
   * True once today balance calculations are ready.
   * Used by Today table, balance HUD, and chart controls.
   */
  readonly todayViewReady = toSignal(
    this.loadingSession$.pipe(
      switchMap(() =>
        this.calculationTodayStore.isCalculationReady$.pipe(
          scan((latched, ready) => latched || ready, false),
          distinctUntilChanged(),
        ),
      ),
    ),
    { initialValue: false },
  );

  /** True once past balance calculations are ready. */
  readonly pastViewReady = toSignal(
    this.loadingSession$.pipe(
      switchMap(() =>
        this.calculationPastStore.isCalculationReady$.pipe(
          scan((latched, ready) => latched || ready, false),
          distinctUntilChanged(),
        ),
      ),
    ),
    { initialValue: false },
  );

  /**
   * True once a scenario cycle has settled (success, empty, or error).
   * Consumes the scenario store's durable {@link ScenarioStoreService.isScenarioReady$} flag rather
   * than the transient `storeState$` `Completed` status (which is synchronously overwritten with
   * `Idle`, so a late subscriber never sees it — PR #3708). The source rests at its value, so no
   * `scan` latch is needed here.
   */
  readonly scenarioViewReady = toSignal(
    this.loadingSession$.pipe(
      switchMap(() => this.scenarioStore.isScenarioReady$.pipe(distinctUntilChanged())),
    ),
    { initialValue: false },
  );
}
